import type { RelatedProductData } from "~/ProductDetails/models/PdpPageProductData";
import { ProductService } from "~/AddToCart/ProductService";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import classes from "~/AddToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal.module.less";
import { userIsCustomer } from "~/utils/roles";
import { ProductEntry } from "./components/ProductEntry";
import type { PurchasePlace, PurchaseType } from "~/feature/Analytics/ga/recommendations/types";
import { Stack, Typography } from "@mui/material";
import { ProductEntrySkeleton } from "./components/ProductEntrySkeleton";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface Props {
  entries: RelatedProductData[];
  title: string;
  "tracking-aux-reco-type": "rsp" | "top";
  "tracking-aux-title"?: string;
  "tracking-aux-location"?: string;
  place: PurchasePlace;
}

export const Recommendations = ({ entries, title, ...trackingProps }: Props) => {
  const reco_type: PurchaseType = trackingProps["tracking-aux-reco-type"];
  const reco_place: PurchasePlace = trackingProps.place;

  const fetchAvailability = reco_type === "top" ? false : true;

  const productDescriptors = ProductService.useMultipleProductDescriptors({
    productCodes: entries.map((entry) => entry.code),
    priceReference: getInitialPriceReference(),
    options: { fetch: true, fetchAvailability },
  });

  const isDesktop = useIsMinDesktopSize();

  const maxItems = isDesktop
    ? window.SCHA.addedToCart.relatedProducts.desktop
    : window.SCHA.addedToCart.relatedProducts.mobile;

  const isProductLoaded = (descriptor: ProductDescriptor): boolean => {
    const hasAvailability = fetchAvailability ? !!descriptor.availability : true;
    return !!descriptor.prices && hasAvailability && !!descriptor.base;
  }

  const productsAreLoading =
    userIsCustomer() && Object.values(productDescriptors).some((descriptor) => !(isProductLoaded(descriptor)));

  return (
    <div className={classes.recommended} {...trackingProps}>
      {entries.length > 0 && (
        <Stack gap={1}>
          <Typography variant="titleMedium">{title}</Typography>

          {productsAreLoading ? (
            <>
              <ProductEntrySkeleton />
              <ProductEntrySkeleton />
            </>
          ) : (
            entries.slice(0, maxItems).map((entry) => {
              return <ProductEntry productCode={entry.code} reco_type={reco_type} reco_place={reco_place} />;
            })
          )}
        </Stack>
      )}
    </div>
  );
};
